import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "components/ui/button";
import { ButtonLink } from "components/ui/button-link";
import { Card, CardContent, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { PasswordInput } from "components/ui/password-input";
import { Spinner } from "components/ui/spinner";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { API_BASE_URL } from "config/apiConfig";
import { auth } from "services/api";
import { GoogleButton } from "components/google-button";
import { Icon } from "components/icons/Icon";
import {
  INTAKE_ASSESSMENT_FLOW_NAME,
  isIntakeOptionalAuthenticatedPath,
} from "constants/flows";
import { type AuthExitReason, consumeStoredAuthExitReason } from "utils/authSession";
import { normalizeAuthenticatedUser } from "utils/authUser";
import { isAuthRevocationSafetyError } from "utils/authTransitions";
import { isNativeMobile } from "mobile/nativeBridge";
import { signInWithNativeGoogle } from "mobile/nativeOAuth";

const AUTH_EXIT_REASON_MESSAGES: Record<AuthExitReason, string> = {
  password_changed: "Your password was updated. Please sign in again.",
  session_expired: "Your session expired. Please sign in again.",
  signed_in_elsewhere: "You signed in from another tab. Please sign in again here.",
  signed_out_elsewhere: "You signed out from another tab.",
};

type LoginLocationState = {
  from?: { pathname?: string; search?: string; hash?: string };
  authExitReason?: AuthExitReason;
};

const BLOCKED_RETURN_PATHS = new Set([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
]);

const getSafeReturnPath = (value?: string | null): string | null => {
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return null;
  }

  try {
    const parsed = new URL(value, window.location.origin);
    if (parsed.origin !== window.location.origin || BLOCKED_RETURN_PATHS.has(parsed.pathname)) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

const getStateReturnPath = (state: LoginLocationState | null): string | null => {
  const returnPath = state?.from?.pathname ?? '';
  const search = state?.from?.search ?? '';
  const hash = state?.from?.hash ?? '';
  return getSafeReturnPath(returnPath ? `${returnPath}${search}${hash}` : null);
};

const getSafeMcpReturnUrl = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  try {
    const apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
    const parsed = new URL(value);
    if (
      parsed.origin !== apiOrigin ||
      parsed.pathname !== '/api/admin/course-factory/oauth/authorize/' ||
      parsed.hash
    ) {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
};

export const loginNavigation = {
  redirect(url: string) {
    window.location.assign(url);
  },
};

export const LogIn: React.FC = () => {
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isSubmitting = useRef(false);
  const nativeGoogleAbort = useRef<AbortController | null>(null);
  const [nativeGooglePending, setNativeGooglePending] = useState(false);
  useEffect(() => () => nativeGoogleAbort.current?.abort(), []);
  const location = useLocation();
  const navigate = useNavigate();
  const { login, clearAuthExitReason } = useAuth();
  const intakeAssessmentFlowPath = `/flows/${INTAKE_ASSESSMENT_FLOW_NAME}`;

  const resolvePostLoginPath = useCallback(
    (hasCompletedIntake: boolean, explicitReturnTo?: string | null) => {
      const explicitReturnPath = getSafeReturnPath(explicitReturnTo);
      const state = (location.state ?? null) as LoginLocationState | null;
      const stateReturnPath = getStateReturnPath(state);
      const requestedPath = explicitReturnPath || stateReturnPath;

      if (!hasCompletedIntake) {
        return requestedPath && isIntakeOptionalAuthenticatedPath(requestedPath)
          ? requestedPath
          : intakeAssessmentFlowPath;
      }

      return requestedPath || '/home';
    },
    [intakeAssessmentFlowPath, location.state],
  );

  useEffect(() => {
    const state = (location.state ?? null) as LoginLocationState | null;
    const reason = state?.authExitReason ?? consumeStoredAuthExitReason();
    if (!reason) {
      return;
    }

    setInfoMessage(AUTH_EXIT_REASON_MESSAGES[reason]);
    clearAuthExitReason();
  }, [clearAuthExitReason, location.state]);

  // Handle OAuth callback from URL parameters
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const authSuccess = urlParams.get('auth_success');
      const authError = urlParams.get('auth_error');
      const code = urlParams.get('code');
      const returnTo = urlParams.get('return_to');
      const mcpReturn = getSafeMcpReturnUrl(urlParams.get('mcp_return'));

      if (authSuccess === 'true' && code) {
        try {
          setInfoMessage('');
          setIsLoading(true);
          const response = await auth.exchangeOAuthCode(code);
          const userData = response.data.user;

          await login(
            response.data.token,
            normalizeAuthenticatedUser(userData),
            {
              method: 'google_oauth',
              recoveryConfirmed: response.data.auth_recovery_performed === true,
              recoverySnapshot: response.authRecoverySnapshot,
            },
          );
          clearAuthExitReason();
          
          window.history.replaceState({}, document.title, window.location.pathname);
          
          if (mcpReturn) {
            loginNavigation.redirect(mcpReturn);
          } else {
            navigate(resolvePostLoginPath(Boolean(userData.has_completed_intake), returnTo));
          }
        } catch (error) {
          console.error('Failed to exchange Google OAuth code:', error);
          setError(
            isAuthRevocationSafetyError(error)
              ? error.message
              : 'Failed to complete Google login',
          );
          window.history.replaceState({}, document.title, window.location.pathname);
        } finally {
          setIsLoading(false);
        }
      } else if (authError) {
        console.error('OAuth error:', authError);
        if (authError === 'account_not_found') {
          setError('No account found for that Google email. Please create an account first.');
        } else {
          setError('Google login failed. Please try again.');
        }
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    handleOAuthCallback();
  }, [clearAuthExitReason, login, navigate, resolvePostLoginPath]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading || isSubmitting.current || !e.currentTarget.reportValidity()) return;

    // Read the browser's fields: password managers may fill them without firing
    // the change events that would update controlled React state.
    const formData = new FormData(e.currentTarget);
    const usernameOrEmail = String(formData.get('email') ?? '');
    const password = String(formData.get('password') ?? '');
    isSubmitting.current = true;
    setError('');
    setInfoMessage('');
    
    let credentialsAccepted = false;
    try {
      setIsLoading(true)
      const response = await auth.login(usernameOrEmail, password);
      credentialsAccepted = true;
      await login(
        response.data.token,
        normalizeAuthenticatedUser(response.data.user),
        {
          method: 'password',
          recoveryConfirmed: response.data.auth_recovery_performed === true,
          recoverySnapshot: response.authRecoverySnapshot,
        },
      );
      clearAuthExitReason();

      const urlParams = new URLSearchParams(window.location.search);
      const mcpReturn = getSafeMcpReturnUrl(urlParams.get('mcp_return'));
      if (mcpReturn) {
        loginNavigation.redirect(mcpReturn);
      } else {
        // Redirect to intake assessment if not completed
        navigate(resolvePostLoginPath(Boolean(response.data.user.has_completed_intake)));
      }
    } catch (err) {
      if (isAuthRevocationSafetyError(err)) {
        setError(err.message);
      } else if (credentialsAccepted) {
        setError('Unable to finish signing in. Please try again.');
      } else {
        setError('Invalid email or password');
      }
    } finally {
      isSubmitting.current = false;
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (isSubmitting.current) return;
    if (isNativeMobile()) {
      const controller = new AbortController();
      nativeGoogleAbort.current = controller;
      isSubmitting.current = true;
      setIsLoading(true);
      setNativeGooglePending(true);
      setError('');
      setInfoMessage('Complete Google sign-in in your browser, then return to WiseFriend.');
      try {
        const response = await signInWithNativeGoogle(controller.signal);
        if (controller.signal.aborted) return;
        await login(response.data.token, normalizeAuthenticatedUser(response.data.user), {
          method: 'google_oauth',
          recoveryConfirmed: response.data.auth_recovery_performed === true,
          recoverySnapshot: response.authRecoverySnapshot,
        });
        if (controller.signal.aborted) return;
        clearAuthExitReason();
        navigate(resolvePostLoginPath(Boolean(response.data.user.has_completed_intake)));
      } catch (failure) {
        if (!controller.signal.aborted) {
          const detail = (failure as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : failure instanceof Error ? failure.message : 'Google sign-in failed. Please try again.');
        }
      } finally {
        if (nativeGoogleAbort.current === controller) {
          nativeGoogleAbort.current = null;
          isSubmitting.current = false;
          setIsLoading(false);
          setNativeGooglePending(false);
          setInfoMessage('');
        }
      }
      return;
    }
    setError('');
    setInfoMessage('');
    
    try {
      await auth.waitForPendingLogout();
      // Use the new token-based OAuth endpoint
      const backendBaseUrl = API_BASE_URL;
      const state = (location.state ?? null) as LoginLocationState | null;
      const returnTo = getStateReturnPath(state);
      const params = new URLSearchParams();
      if (returnTo) {
        params.set('return_to', returnTo);
      }
      if (auth.requiresLogoutRecovery()) {
        params.set('recover_ambiguous_logout', '1');
      }
      const currentParams = new URLSearchParams(window.location.search);
      const mcpReturn = getSafeMcpReturnUrl(currentParams.get('mcp_return'));
      if (mcpReturn) {
        params.set('mcp_return', mcpReturn);
      }
      const query = params.toString();
      loginNavigation.redirect(`${backendBaseUrl}/api/auth/google/initiate/${query ? `?${query}` : ''}`);
    } catch (error) {
      console.error('Failed to initiate Google OAuth:', error);
      setError(
        isAuthRevocationSafetyError(error)
          ? error.message
          : 'Failed to start Google login',
      );
    }
  };

  return (
    <main
      className="flex-1 w-full flex items-center justify-center py-4 sm:px-6 sm:py-12"
      data-model-id="58:7900"
    >
      {/* Phones already get the page gutter, so the card spends its width on the form. */}
      <Card maxWidth="narrow" className="mx-0 max-w-[23.75rem] px-5 sm:mx-4 sm:max-w-[21.25rem] sm:px-6">
        <div className="flex justify-center">
          <Icon name="logo" size={64} />
        </div>
        <CardTitle
          centered={true}
          title={
            <p className="leading-tight">
              Welcome to <br />
              Wise Friend
            </p>
          }
        />
        
        <CardContent variant="form" className="pt-3 sm:pt-4">
          {error && (
            <div
              className="w-full rounded-md text-sm text-red-600 text-center"
              role="alert"
            >
              {error}
            </div>
          )}
          {!error && infoMessage && (
            <div
              className="w-full rounded-md text-xs text-foreground/80 text-center"
              role="status"
            >
              {infoMessage}
            </div>
          )}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="w-full">
              <label
                htmlFor="login-email"
                className="mb-1.5 block text-xs font-semibold text-foreground"
              >
                Email
              </label>
              <Input
                id="login-email"
                name="email"
                size="sm"
                type="email"
                autoComplete="username"
                required
                className="text-base focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:text-[0.8125rem]"
                placeholder="name@example.com"
              />
            </div>

            <div className="flex w-full flex-col gap-1">
              <label
                htmlFor="login-password"
                className="block text-xs font-semibold text-foreground"
              >
                Password
              </label>
              <PasswordInput
                id="login-password"
                name="password"
                size="sm"
                autoComplete="current-password"
                required
                className="text-base focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:text-[0.8125rem]"
                placeholder="Password *"
              />
              <div className="self-end">
                <ButtonLink to="/forgot-password">Forgot password?</ButtonLink>
              </div>
            </div>

            <Button type="submit" variant="default" disabled={isLoading}>
              {isLoading ? (
                <Spinner size="sm" />
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <GoogleButton onClick={handleGoogleLogin} disabled={isLoading} isLoading={nativeGooglePending}/>
          {nativeGooglePending && (
            <Button type="button" variant="outline" onClick={() => nativeGoogleAbort.current?.abort()}>
              Cancel Google sign-in
            </Button>
          )}

          <p className="type-body-s text-foundation-base-darkbase-dark-500 text-[0.6875rem] text-center">
            Don&apos;t have an account?{" "}
            <ButtonLink to="/register" action="sign_up">Sign up</ButtonLink>
          </p>
        </CardContent>
      </Card>
    </main>
  );
};
export default LogIn;









































